import {
  ChangeDetectorRef, Component, Input, OnDestroy, OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProjectsService } from '../../../../../core/projects.service';
import { CommonService } from '../../../../../../../shared/services/common.service';
import {
  FetchContractorsListRequest,
  FetchProjectMembersListRequest,
} from '../../../../../core/projects.actions';
import {
  contractorsListInfo,
  projectMembersListInfo,
} from '../../../../../core/projects.selectors';

@Component({
  selector: 'app-punchlist-settings-form',
  templateUrl: './punchlist-settings-form.component.html',
  styleUrls: ['./punchlist-settings-form.component.scss'],
  standalone: false,
})
export class PunchlistSettingsFormComponent implements OnInit, OnDestroy {
  @Input() data: any;

  form: FormGroup;
  participantSelectForm: FormGroup;
  contractorAccountSelectForm: FormGroup;
  saving = false;
  contractorOptions: any[] = [];
  // All accounts on the project, before exclusion by current selections
  allProjectMemberOptions: any[] = [];
  allContractorAccountOptions: any[] = [];
  // Filtered options shown in dropdowns
  participantOptions: any[] = [];
  contractorAccountOptions: any[] = [];
  // Already-selected lists
  selectedParticipants: any[] = [];
  selectedContractorAccounts: any[] = [];

  errorMessages: Record<string, string> = {};
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private cdRef: ChangeDetectorRef,
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      start_date: [null],
      completion_date: [null],
      substantial_completion: [false],
      prime_contractor_id: [null],
    });
    this.participantSelectForm = this.fb.group({ selectedParticipant: [[]] });
    this.contractorAccountSelectForm = this.fb.group({ selectedContractorAccount: [[]] });
  }

  ngOnInit(): void {
    if (this.data?.id || this.data?.uuid) {
      this.form.patchValue({
        name: this.data.name || '',
        description: this.data.description || '',
        start_date: this.data.start_date || null,
        completion_date: this.data.completion_date || null,
        substantial_completion: !!this.data.substantial_completion,
        prime_contractor_id:
          this.data.prime_contractor_id
          || this.data.primeContractor?.uuid
          || this.data.primeContractor?.id
          || null,
      });
      this.selectedParticipants = (this.data.participants || []).map((p: any) => ({
        account_id: p.uuid || p.id || p.account_id,
        first_name: p.first_name,
        last_name: p.last_name,
        email: p.email,
        role: p.punchlist_role || 'participant',
      }));
      this.selectedContractorAccounts = (this.data.contractorAccounts || []).map((c: any) => ({
        account_id: c.uuid || c.id || c.account_id,
        first_name: c.first_name,
        last_name: c.last_name,
        email: c.email,
        sub_contractor_id: c.sub_contractor_id,
      }));
    } else {
      this.form.patchValue({ name: 'Project punch list' });
    }

    this.store.dispatch(FetchContractorsListRequest({
      payload: { projectId: this.data?.projectId },
    }));
    this.store.dispatch(FetchProjectMembersListRequest({
      payload: { projectId: this.data?.projectId },
    }));

    this.store.select(contractorsListInfo).pipe(takeUntil(this.onDestroy)).subscribe((info: any) => {
      if (!info?.list) return;
      this.contractorOptions = info.list
        .filter((c: any) => c.prime)
        .map((c: any) => ({ value: c.id, label: c.name || c.poc || c.id }));
      if (
        this.contractorOptions.length
        && !this.form.value.prime_contractor_id
        && !this.data?.id
      ) {
        this.form.patchValue({ prime_contractor_id: this.contractorOptions[0].value });
      }
    });

    this.store.select(projectMembersListInfo).pipe(takeUntil(this.onDestroy)).subscribe((info: any) => {
      if (!info?.list) return;
      // Participants: every user on the project (inspectors, PMs, contractors, sub-contractors)
      this.allProjectMemberOptions = info.list.map((m: any) => ({
        value: m.uuid || m.id,
        label: `${m.first_name} ${m.last_name}` + (m.email ? ` (${m.email})` : ''),
        first_name: m.first_name,
        last_name: m.last_name,
        email: m.email,
      }));
      this.updateParticipantOptions();

      // Notification users: contractor-side users on the project
      this.allContractorAccountOptions = info.list
        .filter((m: any) => (m.role_code || m.role || '').startsWith('contractor'))
        .map((m: any) => ({
          value: m.uuid || m.id,
          label: `${m.first_name} ${m.last_name}` + (m.email ? ` (${m.email})` : ''),
          first_name: m.first_name,
          last_name: m.last_name,
          email: m.email,
          sub_contractor_id: this.form.value.prime_contractor_id || null,
        }));
      this.updateContractorAccountOptions();
    });

    // React to dropdown changes — append to selected list and clear
    this.participantSelectForm.get('selectedParticipant')?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((vals: any) => {
        if (!Array.isArray(vals)) return;
        for (const val of vals) {
          const member = this.allProjectMemberOptions.find(m => m.value === val);
          if (member && !this.selectedParticipants.find(a => a.account_id === member.value)) {
            this.selectedParticipants.push({
              account_id: member.value,
              first_name: member.first_name,
              last_name: member.last_name,
              email: member.email,
              role: 'participant',
            });
            this.updateParticipantOptions();
          }
        }
      });

    this.contractorAccountSelectForm.get('selectedContractorAccount')?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((vals: any) => {
        if (!Array.isArray(vals)) return;
        for (const val of vals) {
          const member = this.allContractorAccountOptions.find(m => m.value === val);
          if (member && !this.selectedContractorAccounts.find(a => a.account_id === member.value)) {
            this.selectedContractorAccounts.push({
              account_id: member.value,
              first_name: member.first_name,
              last_name: member.last_name,
              email: member.email,
              sub_contractor_id: member.sub_contractor_id,
            });
            this.updateContractorAccountOptions();
          }
        }
      });

    // When prime contractor changes, update sub_contractor_id on cached options
    this.form.get('prime_contractor_id')?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((primeId: any) => {
        this.allContractorAccountOptions = this.allContractorAccountOptions.map(o => ({
          ...o, sub_contractor_id: primeId || null,
        }));
      });
  }

  updateParticipantOptions() {
    const ids = this.selectedParticipants.map(a => a.account_id);
    this.participantOptions = this.allProjectMemberOptions.filter(m => !ids.includes(m.value));
    this.cdRef.detectChanges();
  }

  updateContractorAccountOptions() {
    const ids = this.selectedContractorAccounts.map(a => a.account_id);
    this.contractorAccountOptions = this.allContractorAccountOptions.filter(m => !ids.includes(m.value));
    this.cdRef.detectChanges();
  }

  removeParticipant(account: any) {
    this.selectedParticipants = this.selectedParticipants.filter(a => a.account_id !== account.account_id);
    this.updateParticipantOptions();
  }

  removeContractorAccount(account: any) {
    this.selectedContractorAccounts = this.selectedContractorAccounts.filter(a => a.account_id !== account.account_id);
    this.updateContractorAccountOptions();
  }

  get isEdit(): boolean { return !!(this.data?.id || this.data?.uuid); }

  checkForErrors(event: { controlName: string; message: string }) {
    if (event?.controlName) this.errorMessages[event.controlName] = event.message;
  }

  submit(): void {
    if (this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    const body = {
      ...this.form.value,
      participants: this.selectedParticipants.map(p => ({
        account_id: p.account_id, role: p.role || 'participant',
      })),
      contractor_accounts: this.selectedContractorAccounts.map(c => ({
        account_id: c.account_id, sub_contractor_id: c.sub_contractor_id || null,
      })),
    };

    const op$ = this.isEdit
      ? this.projectsService.updatePunchlist({
        projectId: this.data.projectId,
        punchlistId: this.data.uuid || this.data.id,
        data: body,
      })
      : this.projectsService.createPunchlist({
        projectId: this.data.projectId,
        data: body,
      });

    op$.pipe(takeUntil(this.onDestroy)).subscribe({
      next: () => {
        this.saving = false;
        this.commonService.notification(
          this.isEdit ? 'Punch list updated' : 'Punch list created',
          'success',
        );
        this.projectsService.punchlistChanged.next();
        this.projectsService.closeRightPanel.next();
      },
      error: (err: any) => {
        this.saving = false;
        this.commonService.errorNotification(err?.error?.error || 'Failed to save punch list');
      },
    });
  }

  deletePunchlist(): void {
    if (!this.isEdit) return;
    if (!confirm('Are you sure you want to delete this punch list? Items will also be soft-deleted.')) return;
    this.saving = true;
    this.projectsService.deletePunchlist({
      projectId: this.data.projectId,
      punchlistId: this.data.uuid || this.data.id,
    }).pipe(takeUntil(this.onDestroy)).subscribe({
      next: () => {
        this.saving = false;
        this.commonService.notification('Punch list deleted', 'success');
        this.projectsService.punchlistChanged.next();
        this.projectsService.closeRightPanel.next();
      },
      error: () => {
        this.saving = false;
        this.commonService.errorNotification('Failed to delete punch list');
      },
    });
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
